import * as React from "react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Paper,
  Avatar,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import axios from '../../Configs/axiosInstance';
import { MAIN_URL } from "../../Configs/Urls";
import PatientDietView from "../PatientDietPage/Patient-Diet-View";
import PatientDietForm from "../PatientDietPage/Patient-Diet-Form";
import CustomizedDataGrid from "../Components/CustomizedDataGrid";
import { colorForName, initialsFor } from "./Components/avatarUtils";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";

function PatientDietPlan() {
  const { id: PatientId } = useParams();
  const navigate = useNavigate();

  const [patient, setPatient] = useState(null);
  const [loadingPatient, setLoadingPatient] = useState(true);

  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  let [addPatientsDiet, setaddPatientsDiet] = React.useState(false);
  let [viewPatientsDiet, setviewPatientsDiet] = React.useState(false);
  let [weight, setWeight] = React.useState("");
  const [plans, setPlans] = React.useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [Deleteloading, setDeleteLoading] = React.useState(false);

  let [PlanId, setPlanId] = React.useState(null);
  let [selectedWeek, setselectedWeek] = React.useState(null);
  let [allWeeks, setAllWeeks] = React.useState(null);

  let [updatePlan, setUpdatePlan] = React.useState(false)

  // Fetch the patient's own header info (name/contact/etc.) — this page is
  // routed now, so it no longer receives Name/ContactNumber as props.
  React.useEffect(() => {
    if (!PatientId) return;
    setLoadingPatient(true);
    axios
      .get(`${MAIN_URL}patients/${PatientId}`)
      .then((response) => setPatient(response.data))
      .catch(() => {
        setSnackbarMessage("Error fetching patient data.");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      })
      .finally(() => setLoadingPatient(false));
  }, [PatientId]);

  // Fetch patients from API
  const fetchPatientsDiet = async () => {
    try {
      setLoading(true); // Start loading
      const response = await axios.get(`${MAIN_URL}patientdietplans/showall/${PatientId}`);
      setPlans(response?.data?.sort((a, b) => b.id - a.id));
      let allweeks = response?.data?.map(item => `${item.weekDateStart} - ${item.weekDateEnd}`);
      setAllWeeks(allweeks || []);

    } catch (error) {
      console.error("Error deleting patient:", error);
      setError(error?.response?.data?.message || "Failed to fetch Diet Plans. Please try again.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  // Fetch patients on component mount
  React.useEffect(() => {
    fetchPatientsDiet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addPatientsDiet]);

  const handleEdit = (row) => {
    setselectedWeek(`${row.weekDateStart} - ${row.weekDateEnd}`)
    setaddPatientsDiet(true);
    setPlanId(row.id)
    setUpdatePlan(true)
  };

  const handleView = (row) => {
    setviewPatientsDiet(true);
    setPlanId(row.id)
    setselectedWeek(`${row.weekDateStart} - ${row.weekDateEnd}`)
    setWeight(row.weight)
  };

  const handleDelete = (row) => {
    setPlanId(row.id);
    setOpenDeleteDialog(true); // Open delete confirmation dialog
  };

  const handleadd = () => {
    setaddPatientsDiet(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${MAIN_URL}patientdietplans/${PlanId}`
      );

      setSnackbarMessage("Patient's Diet Plan deleted successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchPatientsDiet();
    } catch (error) {
      console.error("Error deleting patient:", error);
      setSnackbarMessage(
        error?.response?.data?.message
          ? error?.response.data.message
          : "Error Deleting patient's Diet Plan. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true); // Show Snackbar on error
    } finally {
      setOpenDeleteDialog(false); // Close the dialog
      setDeleteLoading(false);
    }
  };

  const onCloseForm = () => {
    setaddPatientsDiet(false);
    setUpdatePlan(false)
    setviewPatientsDiet(false)
    setPlanId(null)
    setselectedWeek(null)
    setAllWeeks(null)
    setWeight(null)
  };

  const columns = [
    {
      field: "weekDateStart",
      headerName: "Plan Start Date",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "weekDateEnd",
      headerName: "Plan End Date",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "weight",
      headerName: "Weight",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value || "-"}</Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              color="info"
              onClick={(event) => {
                event.stopPropagation();
                handleView(params.row);
              }}
            >
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                handleEdit(params.row);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(params.row);
              }}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const showingForm = addPatientsDiet || viewPatientsDiet;

  return (
    <Box sx={{ width: "100%", maxWidth: 1100 }}>
      <Button
        startIcon={<ArrowBackRoundedIcon />}
        onClick={() => (showingForm ? onCloseForm() : navigate('/patients'))}
        sx={{ mb: 2, textTransform: 'none', fontWeight: 600 }}
      >
        {showingForm ? "Back to Diet Plans" : "Back to Patients List"}
      </Button>

      <Stack spacing={3}>
        {/* Patient summary card */}
        {loadingPatient ? (
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3, borderColor: 'divider' }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 2 }}>
              <Avatar sx={{ width: 56, height: 56, fontSize: '1.2rem', bgcolor: colorForName(patient?.name) }}>
                {initialsFor(patient?.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {patient?.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                  {patient?.gender && <Chip label={patient.gender} size="small" variant="outlined" />}
                  <Typography variant="body2" color="text.secondary">{patient?.contactNumber}</Typography>
                </Stack>
              </Box>
            </Stack>
          </Paper>
        )}

        {viewPatientsDiet ? (
          <PatientDietView
            showDownload={true}
            showPatientInfo={false}
            planId={PlanId}
            onCloseForm={onCloseForm}
            selectedWeekdefault={PlanId == null ? "" : selectedWeek}
            weight={PlanId == null ? "" : weight}
          />
        ) : addPatientsDiet ? (
          <>
            {plans.length > 0 && (
              <PatientDietView
                showDownload={false}
                showPatientInfo={false}
                planId={plans[0]?.id}
                onCloseForm={onCloseForm}
                selectedWeekdefault={plans[0]?.id == null ? "" : `${plans[0]?.weekDateStart} - ${plans[0]?.weekDateEnd}`}
                weight={plans[0]?.weight || "-"}
              />
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 700 }} id="add-patient">
              {PlanId ? "Edit Diet Plan" : "Add New Diet Plan"}
            </Typography>

            <PatientDietForm
              showPatientInfo={false}
              patientId={PatientId}
              onCloseForm={onCloseForm}
              name={patient?.name}
              planId={PlanId}
              selectedPlansWeeks={allWeeks}
              selectedWeekdefault={PlanId == null ? "" : selectedWeek}
            />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Diet Plans
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleadd}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Add Diet Plan
              </Button>
            </Box>

            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "150px" }}>
                <CircularProgress />
              </Box>
            )}

            {!loading && (plans?.length === 0 || error) ? (
              <Paper variant="outlined" sx={{ p: 4, borderRadius: 3, borderColor: 'divider', textAlign: 'center' }}>
                <AssignmentRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">{error || "No diet plans added yet for this patient."}</Typography>
              </Paper>
            ) : !loading && plans.length > 0 && (
              <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}>
                <CustomizedDataGrid rows={plans} columns={columns} />
              </Paper>
            )}
          </>
        )}
      </Stack>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this diet plan?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={Deleteloading}
            variant="contained"
            color="error"
            size="small"
          >
            {Deleteloading ? "Deleting ..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PatientDietPlan;
